import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Card, Empty, Form, message, Modal, notification, Popconfirm, Select, Space, Spin, Table, Tag, Tooltip } from 'antd';

import { cultureAPI } from '../../http/CultureAPI';
import { Culture, CultureSort, UserCulture } from '../../models/Culture';

const { Option } = Select;

// extractErrorMessage: получает текст ошибки из ответа API.
// Поддерживает: строку, объект с message, пустое.
const extractErrorMessage = (e: any, fallback: string): string => {
    const data = e?.response?.data;
    if (typeof data === 'string' && data.trim()) {
        // Может быть JSON-строка — пробуем распарсить
        try {
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
                return parsed.message;
            }
        } catch {
            return data;
        }
        return data;
    }
    if (data && typeof data.message === 'string' && data.message.trim()) {
        return data.message;
    }
    return fallback;
};

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 120000;

const STATUS_CONFIG: Record<string, { label: string; color: string; tip: string }> = {
    culture_pending: { label: 'уточняется', color: 'processing', tip: 'Культура обрабатывается ИИ, обычно 20–30 секунд' },
    sort_pending: { label: 'сорт уточняется', color: 'processing', tip: 'Культура готова, сорт ещё обрабатывается ИИ' },
    fallback: { label: 'по типу культуры', color: 'warning', tip: 'ИИ не дал точных данных, использованы типовые значения. Рекомендуется уточнить.' },
    done: { label: 'готово', color: 'success', tip: 'Данные заполнены' },
};

export default function MyCulturesTab() {
    const [items, setItems] = useState<UserCulture[]>([]);
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    // id, за которыми следим, чтобы не слать повторные уведомления
    const notifiedIds = useRef<Set<number>>(new Set());
    const pollStartedAt = useRef<number>(0);

    const [cultures, setCultures] = useState<Culture[]>([]);
    const [culturesLoading, setCulturesLoading] = useState(false);
    const [selectedCultureId, setSelectedCultureId] = useState<number | undefined>(undefined);
    const [sorts, setSorts] = useState<CultureSort[]>([]);
    const [sortsLoading, setSortsLoading] = useState(false);
    const [selectedSortId, setSelectedSortId] = useState<number | undefined>(undefined);

    const [editCultureId, setEditCultureId] = useState<number | null>(null);
    const [editSorts, setEditSorts] = useState<CultureSort[]>([]);
    const [editSortId, setEditSortId] = useState<number | undefined>(undefined);

    const loadItems = useCallback((): Promise<UserCulture[]> => {
        setLoading(true);
        return cultureAPI
            .GetMyCultures()
            .then((data: any) => {
                const list = (data || []) as UserCulture[];
                setItems(list);
                return list;
            })
            .catch(() => {
                message.error('Не удалось загрузить список');
                return [] as UserCulture[];
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        // Первая загрузка: помечаем все уже готовые записи как «уже уведомлённые»,
        // чтобы polling не слал уведомления по старым культурам.
        loadItems().then((data) => {
            const init = new Set<number>();
            for (const item of data) {
                if (item.enrichment_status === 'done' || item.enrichment_status === 'fallback') {
                    init.add(item.id);
                }
            }
            notifiedIds.current = init;
        });
    }, [loadItems]);

    // Считает, есть ли незавершённые (culture_pending / sort_pending).
    const pendingCount = (list: UserCulture[]) =>
        list.filter((x) => x.enrichment_status === 'culture_pending' || x.enrichment_status === 'sort_pending').length;

    // Polling: пока есть pending — опрашиваем каждые 5 сек, максимум 2 минуты.
    useEffect(() => {
        let cancelled = false;
        let timer: any = null;

        const tick = async () => {
            if (cancelled) return;
            const data = await loadItems();
            const pending = pendingCount(data);

            // Уведомления о завершении
            for (const item of data) {
                if (item.enrichment_status === 'done' && !notifiedIds.current.has(item.id)) {
                    notifiedIds.current.add(item.id);
                    notification.success({
                        message: 'Данные обновлены',
                        description: item.culture?.name ? `Готово: ${item.culture.name}` : 'Обогащение завершено.',
                        placement: 'topRight',
                        duration: 6,
                    });
                } else if (item.enrichment_status === 'fallback' && !notifiedIds.current.has(item.id)) {
                    notifiedIds.current.add(item.id);
                    notification.warning({
                        message: 'Данные обновлены (типовые значения)',
                        description: item.culture?.name
                            ? `ИИ не дал точных данных для «${item.culture.name}». Использованы типовые значения по категории — рекомендуется уточнить вручную.`
                            : 'Использованы типовые значения.',
                        placement: 'topRight',
                        duration: 8,
                    });
                }
            }

            if (pending === 0) {
                return;
            }

            if (Date.now() - pollStartedAt.current > POLL_MAX_MS) {
                return;
            }
            timer = setTimeout(tick, POLL_INTERVAL_MS);
        };

        // если есть pending — начинаем цикл
        const hasPending = items.some(
            (x) => x.enrichment_status === 'culture_pending' || x.enrichment_status === 'sort_pending',
        );
        if (hasPending) {
            if (!pollStartedAt.current) pollStartedAt.current = Date.now();
            timer = setTimeout(tick, POLL_INTERVAL_MS);
        }

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [items, loadItems]);

    const searchCultures = (search: string) => {
        setCulturesLoading(true);
        cultureAPI
            .GetCultures(search)
            .then((data: any) => setCultures(data || []))
            .catch(() => setCultures([]))
            .finally(() => setCulturesLoading(false));
    };

    useEffect(() => {
        if (!selectedCultureId) {
            setSorts([]);
            setSelectedSortId(undefined);
            return;
        }
        setSortsLoading(true);
        cultureAPI
            .GetSorts(selectedCultureId)
            .then((data: any) => setSorts(data || []))
            .catch(() => setSorts([]))
            .finally(() => setSortsLoading(false));
    }, [selectedCultureId]);

    useEffect(() => {
        if (!editCultureId) {
            setEditSorts([]);
            setEditSortId(undefined);
            return;
        }
        cultureAPI
            .GetSorts(editCultureId)
            .then((data: any) => setEditSorts(data || []))
            .catch(() => setEditSorts([]));
    }, [editCultureId]);

    const handleOpenAdd = () => {
        setSelectedCultureId(undefined);
        setSelectedSortId(undefined);
        setCultures([]);
        searchCultures('');
        setAddOpen(true);
    };

    const handleAdd = async () => {
        if (!selectedCultureId) {
            message.warning('Выберите культуру');
            return;
        }
        setSaving(true);
        try {
            const resp = await cultureAPI.AddMyCulture(selectedCultureId, selectedSortId);
            setAddOpen(false);
            loadItems();
            if (resp.enrichment_started) {
                notification.info({
                    message: 'Данные о культуре уточняются',
                    description: 'Обычно это занимает 20–30 секунд. Мы сообщим, когда обогащение завершится.',
                    placement: 'topRight',
                    duration: 6,
                });
                pollStartedAt.current = Date.now();
            } else {
                message.success('Культура добавлена');
            }
        } catch (e: any) {
            message.error(extractErrorMessage(e, 'Не удалось добавить культуру'));
        } finally {
            setSaving(false);
        }
    };

    const handleOpenEdit = (item: UserCulture) => {
        setEditId(item.id);
        setEditCultureId(item.culture_id);
        setEditSortId(item.sort_id);
    };

    const handleSaveEdit = async () => {
        if (!editId) return;
        setSaving(true);
        try {
            const resp = await cultureAPI.UpdateMyCulture(editId, editSortId || null);
            setEditId(null);
            loadItems();
            if (resp.enrichment_started) {
                notification.info({
                    message: 'Данные о сорте уточняются',
                    description: 'Мы сообщим, когда обогащение завершится.',
                    placement: 'topRight',
                    duration: 6,
                });
                pollStartedAt.current = Date.now();
            } else {
                message.success('Сорт обновлён');
            }
        } catch (e: any) {
            message.error(extractErrorMessage(e, 'Не удалось обновить сорт'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await cultureAPI.DeleteMyCulture(id);
            notifiedIds.current.delete(id);
            loadItems();
            message.success('Удалено');
        } catch {
            message.error('Не удалось удалить');
        }
    };

    const columns = [
        {
            title: 'Культура',
            dataIndex: ['culture', 'name'],
            key: 'culture',
            render: (_: any, row: UserCulture) => <span>{row.culture?.name || `#${row.culture_id}`}</span>,
        },
        {
            title: 'Сорт',
            dataIndex: ['sort', 'name'],
            key: 'sort',
            render: (_: any, row: UserCulture) =>
                row.sort?.name ? <Tag color="blue">{row.sort.name}</Tag> : <span style={{ color: '#999' }}>не указан</span>,
        },
        {
            title: 'Статус',
            key: 'status',
            render: (_: any, row: UserCulture) => {
                const key = row.enrichment_status || 'done';
                const cfg = STATUS_CONFIG[key];
                if (!cfg) return null;
                return (
                    <Tooltip title={cfg.tip}>
                        <Tag color={cfg.color}>{cfg.label}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, row: UserCulture) => (
                <Space>
                    <Button size="small" onClick={() => handleOpenEdit(row)}>
                        Изменить сорт
                    </Button>
                    <Popconfirm title="Удалить культуру из списка?" onConfirm={() => handleDelete(row.id)}>
                        <Button size="small" danger>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Card
                title="Мои культуры"
                extra={
                    <Space>
                        <Button onClick={loadItems} loading={loading}>
                            Обновить
                        </Button>
                        <Button type="primary" onClick={handleOpenAdd}>
                            Добавить культуру
                        </Button>
                    </Space>
                }
            >
                {loading && items.length === 0 ? (
                    <Spin />
                ) : items.length === 0 ? (
                    <Empty description="Пока ничего не добавлено" />
                ) : (
                    <Table dataSource={items} columns={columns} rowKey="id" pagination={false} />
                )}
            </Card>

            <Modal
                title="Добавить культуру"
                open={addOpen}
                onCancel={() => setAddOpen(false)}
                onOk={handleAdd}
                confirmLoading={saving}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form layout="vertical">
                    <Form.Item label="Культура" required>
                        <Select
                            showSearch
                            placeholder="Начните вводить название"
                            style={{ width: '100%' }}
                            value={selectedCultureId}
                            onChange={(v) => {
                                setSelectedCultureId(v);
                                setSelectedSortId(undefined);
                            }}
                            onSearch={searchCultures}
                            filterOption={false}
                            notFoundContent={culturesLoading ? <Spin size="small" /> : null}
                        >
                            {cultures.map((c) => (
                                <Option key={c.id} value={c.id}>
                                    {c.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Сорт (опционально)">
                        <Select
                            showSearch
                            placeholder={selectedCultureId ? 'Сорт' : 'Сначала выберите культуру'}
                            style={{ width: '100%' }}
                            disabled={!selectedCultureId}
                            allowClear
                            value={selectedSortId}
                            onChange={(v) => setSelectedSortId(v)}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            notFoundContent={sortsLoading ? <Spin size="small" /> : null}
                        >
                            {sorts.map((s) => (
                                <Option key={s.id} value={s.id}>
                                    {s.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Изменить сорт"
                open={editId !== null}
                onCancel={() => setEditId(null)}
                onOk={handleSaveEdit}
                confirmLoading={saving}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form layout="vertical">
                    <Form.Item label="Сорт (опционально)">
                        <Select
                            showSearch
                            placeholder="Сорт"
                            style={{ width: '100%' }}
                            allowClear
                            value={editSortId}
                            onChange={(v) => setEditSortId(v)}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {editSorts.map((s) => (
                                <Option key={s.id} value={s.id}>
                                    {s.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
